import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useRedirect } from 'react-admin';
import { LlmPromptResponse } from '../../../../interfaces/document';
import referralService from '../../../../services/referral';
import { formatDateString } from '../../../../util/dateFormat';
import { Column, CustomTable } from '../../../common/CustomTable';
import { Row } from '../../../common/CustomTable/types';

const LlmPromptsList: React.FC = () => {
  const [prompts, setPrompts] = useState<LlmPromptResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const redirect = useRedirect();

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await referralService.fetchLlmPrompts();
      if (response) {
        setPrompts(response.prompts);
      }
    } catch (error) {
      console.error('Error fetching LLM prompts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const handleView = (prompt: LlmPromptResponse) => {
    redirect(`/documents/settings/llm-prompts/${prompt.id}`);
  };

  const columns: Column[] = [
    { key: 'id', label: 'ID', minWidth: 30 },
    { key: 'name', label: 'Name', minWidth: 150 },
    { key: 'documentType', label: 'Document Type', minWidth: 120 },
    { key: 'version', label: 'Version', minWidth: 80 },
    { key: 'status', label: 'Status', minWidth: 100 },
    { key: 'created', label: 'Created', minWidth: 140 },
    { key: 'actions', label: 'Details', minWidth: 80, align: 'center' },
  ];

  const rows: Row[] = prompts.map((prompt) => ({
    id: (
      <Typography variant='body2' fontWeight='medium'>
        {prompt.id}
      </Typography>
    ),
    name: (
      <Box>
        <Typography variant='body2' fontWeight='medium'>
          {prompt.name}
        </Typography>
      </Box>
    ),
    documentType: <Typography variant='body2'>{prompt.documentType}</Typography>,
    version: (
      <Typography variant='body2' fontFamily='monospace'>
        {prompt.version}
      </Typography>
    ),
    status: (
      <Chip
        label={prompt.isActive ? 'Active' : 'Inactive'}
        color={prompt.isActive ? 'success' : 'default'}
        size='small'
        variant='filled'
      />
    ),
    created: (
      <Typography variant='body2'>
        {formatDateString(prompt.createdAt, 'MMM dd, yyyy HH:mm:ss')}
      </Typography>
    ),
    actions: (
      <Tooltip title='View Prompt'>
        <IconButton size='small' onClick={() => handleView(prompt)}>
          <VisibilityIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    ),
  }));

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          LLM Prompts
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Manage LLM prompts for document processing and content extraction.
        </Typography>
      </Box>

      <CustomTable
        columns={columns}
        rows={rows}
        loading={loading}
        tableSize='small'
        defaultText='No LLM prompts found'
      />
    </Box>
  );
};

export default LlmPromptsList;
